import { ActionButton, Tooltip, TooltipTrigger } from '@adobe/react-spectrum';
import p from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';

/**
 * styled container
 * @memberof DynamicLengthListWithTooltip
 * @type {styled-component}
 */
const StyledContainer = styled.span`
  > button {
    display: inline-block;
    margin-left: 5px;
    :hover {
      ${({ tooltipBgColor }) =>
        tooltipBgColor
          ? `background-color: var(--spectrum-global-color-${tooltipBgColor});`
          : `background-color: transparent;`}
    }
    :active {
      border-color: transparent;
    }
  }
  span {
    padding: 0 !important;
    font-size: 12px;
    ${({ tooltipTriggerTextColor }) => `
      color: var(--spectrum-global-color-${tooltipTriggerTextColor});
    `}
  }
  cursor: default;
  display: flex;
  align-items: center;
`;

/**
 * Component that displays the specified number of items and groups the remaining ones in a tooltip.
 */
const DynamicLengthListWithTooltip = (props) => {
  const {
    list,
    amountShown,
    natural = false,
    listName = '',
    tooltipVariant = 'info',
    tooltipTriggerTextColor = 'blue-600',
    tooltipBgColor,
    tooltipShowAll = false,
    itemsCategoryName = 'item',
    style = {},
  } = props;
  const [isOverlayActive, setOverlayActive] = useState(false);
  const shownItems = list?.slice(0, amountShown);
  const hiddenItems = list?.slice(amountShown);
  const tooltipItems = tooltipShowAll ? list : hiddenItems;
  const isString = list && typeof list[0] === 'string';
  const tooltipAriaLabel = `Show ${hiddenItems?.length} more ${itemsCategoryName}${
    hiddenItems?.length > 1 ? 's' : ''
  } `;

  return (
    <StyledContainer style={style} tooltipTriggerTextColor={tooltipTriggerTextColor} tooltipBgColor={tooltipBgColor}>
      {isString ? shownItems.join(', ') : shownItems}{' '}
      {hiddenItems?.length > 0 && (
        <TooltipTrigger delay={0} isOpen={isOverlayActive} onOpenChange={setOverlayActive}>
          <ActionButton aria-label={tooltipAriaLabel} isQuiet onPress={() => setOverlayActive(true)}>
            {natural ? ' and' : '  +'} {hiddenItems?.length} more {natural ? listName : ''}
          </ActionButton>
          <Tooltip variant={tooltipVariant}>{isString ? tooltipItems?.join(', ') : tooltipItems}</Tooltip>
        </TooltipTrigger>
      )}
    </StyledContainer>
  );
};

DynamicLengthListWithTooltip.propTypes = {
  /** Array of strings or components that make up the list of items. */
  list: p.oneOfType([p.arrayOf(p.string), p.arrayOf(p.element)]).isRequired,
  /** How many items to display on the page. The remaining ones will be displayed in a tooltip. */
  amountShown: p.number.isRequired,
  /** Either or not to use natural language to deliver information (eg: `and 6 more apps` instead of `+ 6 more`). */
  natural: p.bool,
  /** Name of the list to use in the natural language mode. */
  listName: p.string,
  /** React Spectrum tooltip variant for the tooltip. */
  tooltipVariant: p.oneOf(['inspect', 'positive', 'negative', 'info']),
  /** React Spectrum color for the tooltip trigger text (eg: blue-400). */
  tooltipTriggerTextColor: p.string,
  /** React Spectrum color for the tooltip background (eg: blue-400). */
  tooltipBgColor: p.string,
  /** Either or not to show all list items in the tooltip. */
  tooltipShowAll: p.bool,
};

DynamicLengthListWithTooltip.defaultProps = {
  list: [],
  amountShown: 0,
};

export default DynamicLengthListWithTooltip;
